import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

// modeごとに GET のURLを切り替え
const buildIndexUrl = (mode, pageType) => {
  const base =
    mode === "company" ? "/company/suggestions.json" : "/suggestions.json";

  if (!pageType) return base; // companyは全件取得、userは呼び出し前に止める

  return `${base}?page_type=${encodeURIComponent(pageType)}`;
};

/**
 * pageType に応じて suggestions を取得する共通Hook
 * - mode: "company" | "user"
 * - initialPageType: 初期表示の pageType
 */
export default function useSuggestionsIndex({ mode, initialPageType }) {
  // 現在のpageType
  const [pageType, setPageType] = useState(initialPageType ?? "");

  // 一覧データ
  const [suggestions, setSuggestions] = useState([]);

  // ローディング状態
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  // 一覧を取得する関数
  const refetch = useCallback(async () => {
    // companyは常に取得OK、userはpageTypeがある時だけ取得
    const shouldFetch = mode === "company" || !!pageType;
    if (!shouldFetch) return;

    // company は pageType が空でも取得する（全件）
    setIsSuggestionsLoading(true);
    try {
      const res = await fetch(buildIndexUrl(mode, pageType), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      // 必ず新しい配列参照でセット（再描画保証）
      const next = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setSuggestions([...next]);
    } catch (e) {
      console.error(e);

      // 取得失敗時は空にする
      setSuggestions([]);
      toast.error("サジェストの取得に失敗しました。", {
        toastId: "suggest-fetch-error",
      });
    } finally {
      setIsSuggestionsLoading(false);
    }
  }, [mode, pageType]);

  // userでpageTypeが空になったらクリア
  useEffect(() => {
    if (mode === "user" && !pageType) {
      setSuggestions([]);
      setIsSuggestionsLoading(false);
    }
  }, [mode, pageType]);

  // 初期表示 & pageType変更時に取得
  useEffect(() => {
    refetch();
  }, [refetch]);

  // 外部に公開する値
  return useMemo(
    () => ({
      pageType,
      setPageType,
      suggestions,
      isSuggestionsLoading,
      refetch,
    }),
    [pageType, suggestions, isSuggestionsLoading, refetch],
  );
}
